import { useEffect, useState } from "react";
import { useAccountContext } from "./useAccountContext";
import { useAuthContext } from "./useAuthContext";
import { useSettingsContext } from "./useSettingsContext";
import axios from "axios";
import { v4 as uuidv4 } from 'uuid';

function formatCompanyForApi(company) {
  if (!company) return '';
  if (Array.isArray(company)) {
    return company.map(c => `'${c}'`).join(',');
  }
  return `'${company}'`;
}

export const useCompany = () => {
  const {
    username,
    user,
    clientApi,
    dispatch: authDispatch,
  } = useAuthContext();
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSecondary, setIsLoadingSecondary] = useState(true);
  const { account } = useAccountContext();
  const { isWinAuth } = useSettingsContext();

  const axiosInstance = axios.create({
    baseURL: `${clientApi}`,
    headers: {
      accepts: "application/json",
      "Content-Type": "application/json",
    },
  });

  axiosInstance.interceptors.request.use(
    (request) => {

      const accessToken = user.token;
      if (accessToken) {
        request.headers["Authorization"] = `Bearer ${accessToken}`;
      }
      return request;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    response => response, // Directly return successful responses.
    async error => {
      const originalRequest = error.config;
      if (error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
        try {
          if (!isWinAuth) {
            // Make a request to your auth server to refresh the token.
            const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
            // Store the new access and refresh tokens.
            authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

            // Update the authorization header with the new access token.
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          }
          else {
            const newToken = await acquireSilentToken();
            let tmpUser = user;
            tmpUser.token = newToken;
            authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
            axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          }
          return axios(originalRequest); // Retry the original request with the new access token.
        } catch (refreshError) {
          // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
          console.error("Token refresh failed:", refreshError);
          authDispatch({ type: "LOGOUT" });
          return Promise.reject(refreshError);
        }
      }
      return Promise.reject(error); // For all other errors, return the error as is.
    }
  );

  const getCurrencies = async () => {
    setIsLoadingSecondary(true);
    let currencies = {};
    await axiosInstance
      .get(`${clientApi}api/Company/Currency`)
      .then((response) => {
        currencies = Object.entries(response.data).map(
          ([currency, description]) => ({
            label: currency,
            value: currency,
          })
        );
      })
      .catch((error) => {
        console.log(error.response);
        setIsLoadingSecondary(false);
        return undefined;
      });
    setIsLoadingSecondary(false);
    return currencies;
  };

  const getCompanyData = async (selectedCompany) => {
    let data;
    if (selectedCompany === undefined) return;
    setIsLoading(true);
    await axiosInstance
      .get(`${clientApi}api/Company/Company`, {
        params: { company: selectedCompany },
      })
      .then((response) => {
        data = response.data[0];
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        return undefined;
      });
    return data;
  };

  const getCompanies = async () => {
    let arr = [];
    setIsLoading(true);
    await axiosInstance
      .get(`${clientApi}api/Company/AuthCompanies`, {
        params: { employeeid: account?.employeeId },
      })
      .then((response) => {
        response.data.map((company, index) => {
          arr.push({
            label: company.name,
            value: company.company,
          });

        });
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
        console.log(error.response);
      });
    return arr;
  };

  const getCompaniesFullData = async () => {
    let arr = [];
    await axiosInstance
      .get(`${clientApi}api/Company/AuthCompaniesFullData`, {
        params: { employeeid: account?.employeeId },
      })
      .then((response) => {
        arr = response;
      })
      .catch((error) => {
        console.log(error.response);
      });
    return arr;
  };

  const submitData = async (companyData, showNewCompanyScreen) => {
    try {
      var fetchCmd = showNewCompanyScreen
        ? clientApi + "api/Company/NewCompany"
        : clientApi + "api/Company/EditCompany";
      const response = await axiosInstance.post(fetchCmd, companyData, {
        params: {
          employeeid: account?.employeeId,
        },
      });
      if (response.status === 200) return true;
      else return false;
    } catch (error) {
      console.log(error);
      return false; //should be empty
    }
  }
  const getLimitedBranchData = async (selectedCompany) => {
    let arr = [];
    await axiosInstance.get(`${clientApi}api/Company/Branches`, {
      headers: { Authorization: `Bearer ${user.token}` },
      params: { compid: `'${selectedCompany}'` }
    })
      .then((response) => {
        response.data.map((branch, index) => {
          arr.push(
            {
              label: branch.description,
              value: branch.branch,
            }
          )
        })
      })
      .catch((error) => {
        console.log(error);
      });
    return arr;

  };

  const getbranchesData = async (selectedCompany) => {
    let arr = [];
    await axiosInstance
      .get(`${clientApi}api/Company/Branches`, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { compid: `'${selectedCompany}'` },
      })
      .then((response) => {
        response.data.map((branch, index) => {
          arr.push({
            branch: branch.branch,
            d365Code: branch.d365Code,
            description: branch.description,
            regionalCode: branch.regionalCode,
            company: selectedCompany,
            locationsCnt: branch.hasLocations,
            oldBranchID: branch.branch,
            index: index,
          });
        });
      })
      .catch((error) => {
        console.log(error);
      });
    return arr;
  };

  const getSalarySchemes = async (selectedCompany) => {
    setIsLoading(true);
    let data;
    if (selectedCompany === undefined) return;
    await axiosInstance
      .get(`${clientApi}api/Company/salaryschemes`, {
        params: { company: selectedCompany },
      })
      .then((response) => {
        data = response.data;
      })
      .catch((error) => {
        setIsLoading(false);
        return undefined;
      });
    setIsLoading(false);
    return data;
  };

  const getSalaryComponents = async (salaryschemeId) => {
    setIsLoading(true);
    let data;
    if (salaryschemeId === undefined) {
      setIsLoading(false);
      return;
    }
    await axiosInstance
      .get(`${clientApi}api/Company/salarycomponents`, {
        params: { salaryschemeId: salaryschemeId },
      })
      .then((response) => {
        data = response.data;
      })
      .catch((error) => {
        setIsLoading(false);
        return undefined;
      });
    setIsLoading(false);
    return data;
  };

  const saveSalarySchemes = async (salarySchemes) => {
    let sScheme = [];
    let ret = false;
    const keys = Object.keys(salarySchemes);
    keys.forEach((key) => {
      const nestedObject = salarySchemes[key];
      sScheme.push(nestedObject);
    });
    setIsLoading(true);
    await axiosInstance
      .put(`${clientApi}api/Company/savesalaryschemes`, sScheme, {
        params: { employeeid: account.employeeId, username: account.username },
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });

    setIsLoading(false);
    return ret;
  };

  const saveSalaryComponents = async (salaryComponents) => {
    let components = [];
    let ret = false;
    const keys = Object.keys(salaryComponents);
    keys.forEach((key) => {
      const nestedObject = salaryComponents[key];
      components.push(nestedObject);
    });
    setIsLoading(true);
    await axiosInstance
      .put(`${clientApi}api/Company/savesalarycomponents`, components, {
        params: { employeeid: account.employeeId, username: account.username },
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });

    setIsLoading(false);
    return ret;
  };

  const deleteSalaryScheme = async (salarySchemeId) => {
    setIsLoading(true);
    let ret;
    await axiosInstance
      .delete(`${clientApi}api/Company/deleteSalaryScheme`, {
        params: { username: account.username },
        data: salarySchemeId,
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });
    setIsLoading(false);
    return ret;
  };

  const deleteSalaryComponent = async (salarySchemeId) => {
    setIsLoading(true);
    let ret;
    await axiosInstance
      .delete(`${clientApi}api/Company/deleteSalaryComponent`, {
        params: { username: account.username },
        data: salarySchemeId,
      })
      .then((response) => {
        if (response.status === 200) ret = true;
        else ret = false;
      })
      .catch((error) => {
        console.log(error);
        ret = false;
      });
    setIsLoading(false);
    return ret;
  };

  async function updateBranch(updateExistingBranches, updateSucc) {
    try {
      const response = await axiosInstance.post(`${clientApi}api/Company/UpdateExistingBranches`, updateExistingBranches, {
        params: { employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  async function insertBranch(insertBranches, insertSucc) {
    try {
      const response = await axiosInstance.post(`${clientApi}api/Company/InsertBranch`, insertBranches, {
        params: { employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  async function deleteBranch(deleteBranches, deleteSucc) {
    try {
      const response = await axiosInstance.post(`${clientApi}api/Company/DeleteBranch`, deleteBranches, {
        params: { employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  const getLocationsData = async (selectedCompany, selectedBranch) => {
    let arr = [];
    await axios.get(`${clientApi}api/Company/Locations`, {
      headers: { Authorization: `Bearer ${user.token}` },
      params: { company: `'${selectedCompany}'`, branch: `'${selectedBranch}'` }
    })
      .then((response) => {
        arr = response.data.map((location, index) => ({
          location: location.location,
          branch: selectedBranch,
          description: location.description,
          tel: location.tel,
          fax: location.fax,
          addr: location.addr,
          city: location.city,
          postCode: location.postCode,
          province: location.province,
          country: location.country,
          company: selectedCompany,
          d365Code: location.d365Code,
          locationDME: location.locationDME,
          index: index,
          oldLocationID: location.location
        }))
      })
      .catch((error) => {
        // setIsLoading(false);
      });
    return arr;
  };

  const getLimitedLocationData = async (selectedCompany, selectedBranch) => {
    let arr = [];
    await axios.get(`${clientApi}api/Company/Locations`, {
      headers: { Authorization: `Bearer ${user.token}` },
      params: { company: `'${selectedCompany}'`, branch: `'${selectedBranch}'` }
    })
      .then((response) => {
        response.data.map((location, index) => {
          arr.push(
            {
              label: location.description,
              value: location.location,
            }
          )
        })
      })
      .catch((error) => {
        console.log(error);
      });
    return arr;

  };

  async function updateLocation(updateExistingLocations, selectedCompany) {
    try {
      const response = await axios.post(`${clientApi}api/Company/updateExistingLocations`, updateExistingLocations, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { compid: selectedCompany, employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  async function insertLocation(insertLocations, selectedCompany) {
    try {
      const response = await axios.post(`${clientApi}api/Company/InsertLocation`, insertLocations, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { compid: selectedCompany, employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  async function deleteLocation(deleteLocations) {
    try {
      const response = await axios.post(`${clientApi}api/Company/DeleteLocation`, deleteLocations, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  async function getFunctionsData(selectedCompany) {
    let arr = [];
    await axios.get(`${clientApi}api/Company/Functions`, {
      headers: { Authorization: `Bearer ${user.token}` },
      params: { compid: selectedCompany }
    })
      .then((response) => {
        arr = response.data.map((funct, index) => ({
          servCode: funct.servCode,
          description: funct.description,
          headOfFunction: funct.headOfFunction,
          exceedInvAmount: funct.exceedInvAmount,
          regionalCode: funct.regionalCode,
          d365Code: funct.d365Code,
          oldFunctionID: funct.servCode,
          servLinesCnt: funct.hasServiceLines,
          functionID: funct.functionID
        }))
      })
      .catch((error) => {
        console.log(error);
        return false;
      });
    return arr;
  };

  const getAllEmployees = async (selectedCompany) => {
    let data = [];
    await axios.get(`${clientApi}api/Employee/AuthEmployees`, {
      headers: { Authorization: `Bearer ${user.token}` },
      params: { companies: selectedCompany, employeeId: account.employeeId, withPhoto: false }
    })
      .then((response) => {
        response.data.forEach((obj) => {
          data.push({ label: obj.name, value: obj.employeeID });
        });
        return data;
      })
      .catch((error) => {
        console.log(error);
        return false;
      });
    return data;
  }

  async function updateFunctions(updateExistingFunctions, compid) {
    try {
      const response = await axios.post(`${clientApi}api/Company/UpdateExistingFunctions`, updateExistingFunctions, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { employeeid: account.employeeId, compid: compid }
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  async function insertFunction(insertFunctions, selectedCompany) {
    try {
      const response = await axios.post(`${clientApi}api/Company/InsertFunction`, insertFunctions, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { employeeid: account.employeeId, compid: selectedCompany }
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  async function deleteFunction(deleteFunctions) {
    try {
      const response = await axios.post(`${clientApi}api/Company/DeleteFunction`, deleteFunctions, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  async function getServiceLinesData({ props }) {
    let arr = [];
    await axios.get(`${clientApi}api/Company/ServiceLines`, {
      headers: { Authorization: `Bearer ${user.token}` },
      params: { compid: props.selectedCompany, topServCode: props.selectedFunction }
    })
      .then((response) => {
        arr = response.data.map((servLine, index) => ({
          servCode: servLine.servCode,
          servLevel: servLine.servLevel,
          description: servLine.description,
          headID: servLine.headID,
          regionalCode: servLine.regionalCode,
          d365Code: servLine.d365Code,
          topServCode: servLine.topServCode,
          topServLevel: servLine.topServLevel,
          oldServCode: servLine.servCode,
          departmentsCnt: servLine.departmentsCnt,
          servLineID: servLine.servLineID
        }))
      })
      .catch((error) => {
        return false;
      });
    return arr;
  };
  async function updateServLines({ props }, updateExistingServLine) {
    try {
      const response = await axios.post(`${clientApi}api/Company/UpdateExistingServiceLines`, updateExistingServLine, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { compid: props.selectedCompany, employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      console.log(error)
      return false;
    }
  };

  async function insertServLines({ props }, insertServLine) {
    try {
      const response = await axios.post(`${clientApi}api/Company/InsertServLines`, insertServLine, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { employeeid: account.employeeId, compid: props?.selectedCompany, topServCode: props?.selectedFunction }
      });
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  async function deleteServLines(deleteServLine) {
    try {
      const response = await axios.post(`${clientApi}api/Company/DeleteServLines`, deleteServLine, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      console.log(error)
      return false;
    }
  };

  async function getDepartmentsData({ props }) {
    let arr = [];
    await axios.get(`${clientApi}api/Company/DeptsByServLine`, {
      headers: { Authorization: `Bearer ${user.token}` },
      params: { company: props.selectedCompany, servCode: props.selectedServCode, servLevel: props.selectedServLevel }
    })
      .then((response) => {
        arr = response.data.map((dept, index) => ({
          dept: dept.dept,
          description: dept.description,
          hod: dept.hod,
          functionCode: dept.functionCode,
          functionDescription: dept.functionDescription,
          chargesCode: dept.chargesCode,
          regionalCode: dept.regionalCode,
          d365Code: dept.d365Code,
          d365FDCode: dept.d365FDCode,
          oldDepartmentID: dept.dept,
          deptID: dept.deptID
        }))
      })
      .catch((error) => {
        console.log(error)
      });
    return arr;
  };

  async function getPhaseCodes(arr, { props }) {
    let res;
    await axios.get(`${clientApi}api/Company/AdmChargesDept`, {
      headers: { Authorization: `Bearer ${user.token}` },
      params: { company: props.selectedCompany }
    })
      .then((response) => {
        res = response.data;

      })
      .catch((error) => {

      });
    return res;
  };

  async function updateDepartment({ props }, updateExistingDepts) {
    try {
      const response = await axios.post(`${clientApi}api/Company/UpdateExistingDepartments`, updateExistingDepts, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { compid: props.selectedCompany, employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      console.log(error)
      return false;
    }
  };

  async function insertDepartment({ props }, insertDepts) {
    try {
      const response = await axios.post(`${clientApi}api/Company/InsertDepartments`, insertDepts, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: {
          employeeid: account.employeeId, compid: props?.selectedCompany,
          servCode: props?.selectedServCode, servLevel: props?.selectedServLevel
        }
      });
      return true;
    } catch (error) {
      console.log(error)
      return false;
    }
  };

  async function deleteDepartment(deleteDepts) {
    try {
      const response = await axios.post(`${clientApi}api/Company/DeleteDepartments`, deleteDepts, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { employeeid: account.employeeId }
      });
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const getL4 = async ({ props }) => {
    let response = null;
    try {
      response = await axios.get(`${clientApi}api/Company/L4`, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { compid: props?.selectedCompany }
      });
    } catch (error) {
      console.log(error);
    }
    return response;
  };

  const getDepartments = async ({ props }) => {
    let response = null;
    try {
      response = await axios.get(`${clientApi}api/Company/AuthDeptsByCompany`, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { company: props?.selectedCompany, employeeid: account?.employeeId }
      });
    } catch (error) {
      console.log(error);
    }
    return response;
  };

  const getServiceLines = async (selectedCompany) => {
    let response = null;
    try {
      response = await axios.get(`${clientApi}api/Company/ServiceLines`, {
        headers: { Authorization: `Bearer ${user.token}` },
        params: { compid: selectedCompany }
      });
    } catch (error) {
      console.log(error);
    }
    return response;
  };
  const uploadBatch = async (batch, selectedTable) => {
    console.log(batch);
    let data = new FormData();
    data.append('batch', JSON.stringify(batch));
    data.append('tableName', selectedTable?.tableName);
    try {
      const response = await axios.post(`${clientApi}api/Company/UploadData`, data, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        params: { employeeID: account.employeeId }
      });
      return response;
    } catch (error) {
      console.error('Error uploading batch:', error);
      return false;
    }
  };

  const generateExcelData = async (tableName, description) => {
    try {
      const response = await axios.get(`${clientApi}api/Company/ExcelColumnNames`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        params: { tableName: tableName }
      });
      return response;
    } catch (error) {
      console.error('Error uploading batch:', error);
      return false;
    }
  };

  const dropdownValues = async (tableName, description) => {
    try {
      const response = await axios.get(`${clientApi}api/Company/DropdownValues`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        params: { tableName: tableName }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading batch:', error);
    }
  };

  //#region UserRoles
  const getUserRoles = async () => {
    let response = null;
    try {
      response = await axiosInstance.get(`${clientApi}api/Company/UserRoles`);
    } catch (error) {
      console.log(error);
    }
    return response;
  };
  //#endregion

  //#region Workflows
  const getWorkflows = async ({ company }) => {
    let ret;
    const formattedCompany = formatCompanyForApi(company);
    await axiosInstance.get(`${clientApi}api/Employee/GetWorkflows`, {
      params: {
        username: username,
        company: formattedCompany,
        //  [company]?.map(item => `'${item}'`).toString()
      },
    })
      .then((response) => {
        if (response.status === 200)
          ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  }

  const getWorkflowCategories = async () => {
    let ret;
    await axiosInstance.get(`${clientApi}api/Employee/GetWorkflowCategories`, {
      params: {
        username: username,
      },
    })
      .then((response) => {
        if (response.status === 200)
          ret = response;
        else ret = [];
      })
      .catch((error) => {
        console.log(error);
        ret = [];
      });

    return ret;
  }

  const getEditWorkflow = async (company, workflowId) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/EditWorkflows`, {
        params: {
          company, workflowId,
          username: username,
        },
      });
      if (response.status === 200) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching workflow:', error);
      return null;
    }
  };

  const saveWorkflows = async (companyId, workflows) => {
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Company/SaveWorkflows`,
        workflows,
        {
          params: {
            companyId: companyId,
            userId: account.employeeId, username: account.username
          }
        }
      );
      if (response.status === 200) {
        return response.data;
      } else {
        console.error('Failed to save workflows:', response);
        return null;
      }
    } catch (error) {
      console.error('Error saving workflows:', error);
      return null;
    }
  };

  const deleteWorkflows = async (workflowId) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post(`${clientApi}api/Company/DeleteWorkflows`, null, {
        params: {
          username: account.username,
          workflowId: workflowId
        },
      });
      setIsLoading(false);
      if (response.status === 200) {
        return true;
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      setIsLoading(false);
      throw error;
    }
  };

  const hasWorkflowSteps = async (workflowId) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/HasWorkflowSteps`, {
        params: { workflowId },
      });
      if (response.status === 200) {
        return response.data.hasSteps;
      }
      return false;
    } catch (error) {
      console.error('Error checking workflow steps:', error);
      return false;
    }
  };

  const getWorkflowSteps = async (company, workflowId) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/GetWorkflowSteps`, {
        params: { company, workflowId },
      });
      if (response.status === 200) {
        return response.data;
      }
      return false;
    } catch (error) {
      console.error('Error fetching workflow steps:', error);
      throw error;
    }
  };


  const normalizeCategoryId = (categoryId) => {
    if (!categoryId) return categoryId;
    switch (categoryId.toLowerCase()) {
      case 'la':
        return 'leave';
      case 'ts':
        return 'timesheet';
      case 'te':
        return 'travellingexpenses';
      case 'rtn':
        return 'retentions';
      case 'hr':
        return 'hrchanges';
      default:
        return categoryId.toLowerCase();
    }
  };

  const getGeneralApprovers = async (categoryId) => {
    const normalizedCategoryId = normalizeCategoryId(categoryId);
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/GetGeneralApprovers`, {
        params: { categoryId: normalizedCategoryId, username: account.username, },
      });
      if (response.status === 200) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching general approvers:', error);
      throw error;
    }
  };

  const getSpecificApprovers = async (companyId) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/GetSpecificApprovers`, {
        params: { companyId },
      });
      if (response.status === 200) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching specific approvers:', error);
      throw error;
    }
  };

  const saveWorkflowStep = async (companyId, workflowId, step) => {
    try {
      const response = await axiosInstance.post(`${clientApi}api/Company/SaveWorkflowStep`,
        step,
        {
          params: { companyId, workflowId }
        }
      );
      if (response.status === 200) {
        return response.data;
      } else {
        console.error('Failed to save workflows:', response);
        return null;
      }
    } catch (error) {
      console.error("Error saving workflow step:", error);
      return null;
    }
  };

  const deleteWorkflowStep = async (companyId, workflowId, stepNo) => {
    try {
      const response = await axiosInstance.delete(
        `${clientApi}api/Company/DeleteWorkflowStep`,
        {
          params: { companyId, workflowId, stepNo }
        }
      );
      if (response.status === 200) {
        return { success: true, message: response.data.message || `Step ${stepNo} deleted successfully.` };
      }
      return { success: false, message: "Failed to delete workflow step." };
    } catch (error) {
      console.error("Error deleting workflow step:", error);
      return { success: false, message: error.response?.data || "Internal server error" };
    }
  };


  const deleteAllWorkflowSteps = async (companyId, workflowId) => {
    try {
      const response = await axiosInstance.delete(
        `${clientApi}api/Company/DeleteAllWorkflowSteps`,
        {
          params: { companyId, workflowId }
        }
      );
      if (response.status === 200) {
        return { success: true, message: response.data.message || "All workflow steps deleted successfully." };
      }
      return { success: false, message: "Failed to delete all workflow steps." };
    } catch (error) {
      console.error("Error deleting all workflow steps:", error);
      return { success: false, message: error.response?.data || "Internal server error" };
    }
  };

  const validateAndParseApprovalExpression = async (approvalExpr, companyId) => {
    if (!approvalExpr) return { isValid: true, elements: [] };
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Company/ValidateAndParseApprovalExpression`,
        JSON.stringify(approvalExpr), // Send raw string as JSON string
        {
          headers: {
            'Content-Type': 'application/json',
            // Authorization header is added automatically by axiosInstance interceptor
          },
          params: { companyId }
        }
      );
      return { isValid: true, elements: response.data.elements || [] };
    } catch (error) {
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        'Validation error occurred';
      return { isValid: false, error: errorMsg };
    }
  };

  const getWorkflowCategoryCriteria = async (categoryId) => {
    if (!categoryId) return [];

    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/GetWorkflowCategoryCriteria`, {
        params: { categoryId },
      });
      if (response.status === 200) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch workflow category criteria:", error);
      return [];
    }
  };

  const getCriteriaOptionsById = async (sql, company) => {
    if (!sql || !company) return [];
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/GetCriteriaOptionsById`, {
        params: { sql: sql, company: company },
      });
      if (response.status === 200) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch criteria options by id:', error);
      return [];
    }
  };

  const getWorkflowCriteriaDesc = async (company, workflowId) => {
    if (!company || !workflowId) return [];
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/GetWorkflowCriteriaDesc`, {
        params: { company, workflowId },
      });
      if (response.status === 200 && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching workflow criteria description:', error);
      return [];
    }
  };

  const saveWorkflowCriteria = async (companyId, workflowId, criteriaSql, criteriaDesc, criteriaList = []) => {
    try {
      const listToSend = Array.isArray(criteriaList) ? criteriaList : [];

      const response = await axiosInstance.post(`${clientApi}api/Company/SaveCriteria`,
        listToSend,
        {
          params: {
            userid: account.employeeId,
            company: companyId,
            workflowId: workflowId,
            criteriaSql: criteriaSql,
            criteriaDesc: criteriaDesc,
          },
        }
      );
      if (response.status === 200) {
        return response.data;
      } else {
        console.error('Failed to save workflow criteria:', response);
        return false;
      }
    } catch (error) {
      console.error('Error saving workflow criteria:', error.response?.data || error.message);
      return false;
    }
  };

  const copyWorkflowSteps = async (companyId, workflowId, targetCompanyId, targetWorkflowId, username) => {
    try {
      const response = await axiosInstance.post(
        `${clientApi}api/Company/CopyWorkflowSteps`,
        null,
        {
          params: {
            companyId: companyId,
            workflowId: workflowId,
            targetCompanyId: targetCompanyId,
            targetWorkflowId: targetWorkflowId,
            username: username
          },
        }
      );
      if (response.status === 200) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: "Failed to copy workflow steps." };
      }
    } catch (error) {
      console.error("Error copying workflow steps:", error.response || error.message);
      return {
        success: false,
        message: error.response?.data || "Internal server error",
      };
    }
  };
  //#endregion

  //#region Job Titles
  const getCompanyJobTitles = async (selectedCompanyCode) => {
    if (!selectedCompanyCode) return [];
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/GetCompanyJobTitles`, {
        params: { companyCode: selectedCompanyCode, username: username },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch job titles:", error);
      return [];
    }
  };

  const saveJobTitles = async (jobTitles, selectedCompanyCode) => {
    let ret = false;
    setIsLoading(true);
    try {
      const response = await axiosInstance.put(
        `${clientApi}api/Company/SaveJobTitles`,
        jobTitles,
        {
          params: {
            companyCode: selectedCompanyCode,
            userId: account.employeeId, username: account.username
          },
        }
      );
      ret = response.status === 200;
    } catch (error) {
      console.error("Failed to save job titles:", error.response?.data || error.message);
      ret = false;
    }
    setIsLoading(false);
    return ret;
  };

  const deleteJobTitles = async (jobTitles, selectedCompanyCode) => {
    if (!selectedCompanyCode || !Array.isArray(jobTitles) || jobTitles.length === 0) {
      return false;
    }
    try {
      const response = await axiosInstance.delete(
        `${clientApi}api/Company/DeleteJobTitles`,
        {
          data: { jobTitles: jobTitles },
          params: {
            companyCode: selectedCompanyCode,
            username: account.username
          },
        }
      );
      return response.status === 200;
    } catch (error) {
      console.error("Failed to delete job titles:", error.response?.data || error.message);
      return false;
    }
  };
  //#endregion

  //#region Staff Levels
  const getCompanyStaffLevels = async (selectedCompanyCode) => {
    try {
      const url = `${clientApi}api/Hierarchies/Levels/${selectedCompanyCode}`;
      const response = await axiosInstance.get(url, {
        params: { username: username },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch staff levels:", error);
      return [];
    }
  };

  const getSeniorityLevels = async (selectedCompanyCode, hcode) => {
    try {
      const url = `${clientApi}api/Hierarchies/Levels/${selectedCompanyCode}/${hcode}`;
      const response = await axiosInstance.get(url, {
        params: { username: username },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch seniority levels:", error);
      return [];
    }
  };

  const saveStaffLevels = async (staffLevelRequest) => {
    try {
      const url = `${clientApi}api/Hierarchies/Levels`;
      const response = await axiosInstance.post(url, staffLevelRequest, {
        params: { username: username },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error("API error response:", error.response.data);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Error setting up request:", error.message);
      }
      return null;
    }
  };

  const updateStaffLevels = async (staffLevelRequest) => {
    try {
      const url = `${clientApi}api/Hierarchies/Levels`;
      const response = await axiosInstance.put(url, staffLevelRequest, {
        params: { username: username },
      });
      return response.data;
    } catch (error) {
      if (error.response) {
        console.error("API error response:", error.response.data);
      } else if (error.request) {
        console.error("No response received:", error.request);
      } else {
        console.error("Error setting up request:", error.message);
      }
      return null;
    }
  };

  const deleteStaffLevel = async (hcode, company) => {
    try {
      const url = `${clientApi}api/Hierarchies/Levels`;
      await axiosInstance.delete(url, {
        params: { username },
        data: { HCode: hcode, Company: company }
      });
      return { success: true };
    } catch (error) {
      if (error.response && error.response.status === 409) {
        return { success: false, conflict: true, details: error.response.data };
      }
      return { success: false, conflict: false, error };
    }
  };

  // Create a new seniority level
  const createSeniorityLevel = async (req) => {
    try {
      const response = await axiosInstance.post(`${clientApi}api/Hierarchies/Seniority`, req, {
        params: { username: username },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to create seniority level:", error.response?.data || error.message);
      throw error;
    }
  };

  // Update an existing seniority level
  const updateSeniorityLevel = async (req) => {
    try {
      const response = await axiosInstance.put(`${clientApi}api/Hierarchies/Seniority`, req, {
        params: { username: username },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to update seniority level:", error.response?.data || error.message);
      throw error;
    }
  };

  // Delete a seniority level
  const deleteSeniorityLevel = async (req) => {
    try {
      const response = await axiosInstance.delete(`${clientApi}api/Hierarchies/Seniority`, { params: { username: username }, data: req });
      return { success: true, data: response.data };
    } catch (error) {
      if (error.response && error.response.status === 409) {
        // Conflict error with details about usage
        return { success: false, conflict: true, details: error.response.data };
      }
      console.error("Failed to delete seniority level:", error.response?.data || error.message);
      return { success: false, conflict: false, error };
    }
  };
  //#endregion

  //#region Company Auth
  const getAuthorizedUsers = async (selectedCompany) => {
    if (!selectedCompany) return [];
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/AuthorizedUsers`, {
        params: { company: selectedCompany }
      });
      if (response.status === 200 && Array.isArray(response.data)) {

        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching authorised users:', error.response || error.message);
      return [];
    }
  };

  const getRemainingUsers = async (selectedCompany) => {
    if (!selectedCompany) return [];
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/RemainingUsers`, {
        params: { company: selectedCompany }
      });
      if (response.status === 200 && Array.isArray(response.data)) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching remaining users:', error.response || error.message);
      return [];
    }
  };

  const saveAuthorizedUsers = async (payload) => {
    if (!payload?.company) return false;
    try {

      const body = {
        Company: payload.company,
        UsersToAdd: payload.usersToAdd ?? [],
        UsersToRemove: payload.usersToRemove ?? []
      };

      const response = await axiosInstance.post(
        `${clientApi}api/Company/SaveAuthorizedUsers`,
        body,
        { params: { employeeid: account?.employeeId } }
      );
      return response.status === 200;
    } catch (error) {
      console.error('Error saving authorised users:', error.response || error.message);
      return false;
    }
  };

  const getEmployeePhoto = async (employeeId) => {
    if (!employeeId) return null;
    try {
      const response = await axiosInstance.get(`${clientApi}api/Employee/EmployeePhoto/${employeeId}`, {
        responseType: 'arraybuffer',
      });
      if (response.status === 200) {
        const base64 = btoa(
          new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );
        return `data:image/jpeg;base64,${base64}`;
      }
      return null;
    } catch (error) {
      console.error('Error fetching employee photo:', error.response || error.message);
      return null;
    }
  };

  const getStandardRates = async (company) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/StandardRates`, {
        params: { username, userid: account.employeeId, company },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch standard rates :", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * @param {Object} data - The standard rates request payload.
   * @param {string} data.Company - Company code. Required. Maximum 5 characters.
   * @param {string} [data.Description] - Description of the standard rate. Maximum 50 characters.
   * @param {boolean} [data.DefRate] - Indicates whether this is the default rate.
   * @param {string} data.RateId - Rate identifier. Required. Maximum 1 character.
   */
  const postStandardRates = async (data) => {
    try {
      const response = await axiosInstance.post(`${clientApi}api/Company/StandardRates`, data, {
        params: { username, userid: account.employeeId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to post standard rates :", error.response?.data || error.message);
      throw error;
    }
  };
  /**
   * @param {Object} data - The standard rates request payload.
   * @param {string} data.Company - Company code. Required. Maximum 5 characters.
   * @param {string} [data.Description] - Description of the standard rate. Maximum 50 characters.
   * @param {boolean} [data.DefRate] - Indicates whether this is the default rate.
   * @param {string} data.RateId - Rate identifier. Required. Maximum 1 character.
   * @param {string} data.RowGuid - Rate guidentifier.
   */
  const putStandardRates = async (data) => {
    try {
      const response = await axiosInstance.put(`${clientApi}api/Company/StandardRates`, data, {
        params: { username, userid: account.employeeId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to put standard rates :", error.response?.data || error.message);
      throw error;
    }
  };
  /**
   * @param {Object} data - The standard rates request payload.
   * @param {string} data.Company - Company code. Required. Maximum 5 characters.
   * @param {string} data.RateId - Rate identifier. Required. Maximum 1 character.
   */
  const deleteStandardRates = async (data) => {
    try {
      const response = await axiosInstance.delete(`${clientApi}api/Company/StandardRates`, {
        data,
        params: { username, userid: account.employeeId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to put standard rates :", error.response?.data || error.message);
      throw error;
    }
  };

  const getEmployeeRates = async (company, employeeId) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/EmployeeRates`, {
        params: { username, userid: account.employeeId, company, employeeId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch Employee rates :", error.response?.data || error.message);
      throw error;
    }
  };
  const getPrevEmployeeRates = async (company, employeeId) => {
    try {
      const response = await axiosInstance.get(`${clientApi}api/Company/EmployeeRates`, {
        params: { username, userid: account.employeeId, previous: true, company, employeeId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to fetch Employee rates :", error.response?.data || error.message);
      throw error;
    }
  };

  /**
   * @param {Object} data - Employee rate payload.
   * @param {string} data.EmployeeId - Employee identifier. Required. Maximum 9 characters.
   * @param {string} data.RateId - Rate identifier. Required. Maximum 1 character.
   * @param {number} data.Rate - Rate value.
   * @param {string|Date} data.Dor - Date of record/effective date. Required, if passing string has to be ISO format YYYY-MM-DD
   * @param {string} data.Company - Company code. Required. Maximum 5 characters.
   * @param {?string} [data.Comments] - Optional comments. Maximum 250 characters.
   */
  const postEmployeeRates = async (data) => {
    try {
      const response = await axiosInstance.post(`${clientApi}api/Company/EmployeeRates`, data, {
        params: { username, userid: account.employeeId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to post Employee rates :", error.response?.data || error.message);
      throw error;
    }
  };
  /**
   * @param {Object} data - The Employee rates request payload.
   * @param {string} data.Company - Company code. Required. Maximum 5 characters.
   * @param {string} [data.Description] - Description of the Employee rate. Maximum 50 characters.
   * @param {boolean} [data.DefRate] - Indicates whether this is the default rate.
   * @param {string|Date} data.Dor - Date of record/effective date. Required, if passing string has to be ISO format YYYY-MM-DD
   * @param {string} data.RateId - Rate identifier. Required. Maximum 1 character.
   * @param {string} data.RowGuid - Rate guidentifier.
   */
  const putEmployeeRates = async (data) => {
    try {
      const response = await axiosInstance.put(`${clientApi}api/Company/EmployeeRates`, data, {
        params: { username, userid: account.employeeId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to put Employee rates :", error.response?.data || error.message);
      throw error;
    }
  };
  /**
   * @param {Object} data - The Employee rates request payload.
   * @param {string} data.RowGuid - Rate guidentifier.
   */
  const deleteEmployeeRates = async (data) => {
    try {
      const response = await axiosInstance.delete(`${clientApi}api/Company/EmployeeRates`, {
        data,
        params: { username, userid: account.employeeId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to delete Employee rates :", error.response?.data || error.message);
      throw error;
    }
  };
  //#endregion
  //#endregion


  return {
    getEmployeeRates, postEmployeeRates, putEmployeeRates, deleteEmployeeRates, getPrevEmployeeRates,
    getStandardRates, postStandardRates, putStandardRates, deleteStandardRates,
    getCurrencies, getCompanyData, getCompanies, submitData,                                    // Company
    getFunctionsData, getAllEmployees, insertFunction, deleteFunction, updateFunctions,         // Functions
    getServiceLinesData, insertServLines, updateServLines, deleteServLines,                     // Service Lines
    getDepartmentsData, getPhaseCodes, updateDepartment, insertDepartment, deleteDepartment,     // Departments
    getbranchesData, insertBranch, deleteBranch, updateBranch,
    getLocationsData, insertLocation, deleteLocation, updateLocation,
    getSalarySchemes, getSalaryComponents, saveSalarySchemes, saveSalaryComponents, deleteSalaryScheme, getLimitedBranchData,
    deleteSalaryComponent, getL4, getDepartments, getServiceLines,
    uploadBatch, generateExcelData, dropdownValues, getUserRoles, getWorkflows, getWorkflowCategories,
    getCompaniesFullData, getLimitedLocationData, isLoading, isLoadingSecondary, getCompanyJobTitles, saveJobTitles, deleteJobTitles,
    getCompanyStaffLevels, saveStaffLevels, updateStaffLevels, deleteStaffLevel, getSeniorityLevels, createSeniorityLevel, updateSeniorityLevel, deleteSeniorityLevel,
    getEditWorkflow, saveWorkflows,
    deleteWorkflows, hasWorkflowSteps, getWorkflowSteps, getGeneralApprovers, getSpecificApprovers, saveWorkflowStep, deleteWorkflowStep, deleteAllWorkflowSteps,
    validateAndParseApprovalExpression,
    saveWorkflowCriteria, getWorkflowCategoryCriteria, getCriteriaOptionsById, getWorkflowCriteriaDesc, copyWorkflowSteps,
    getAuthorizedUsers, getRemainingUsers, saveAuthorizedUsers, getEmployeePhoto
  }
}